/**
 * @file What a list page looked like, and which rows it showed — so a detail
 * page can offer Prev / Next in the SAME order (sort, filter and search
 * included), and the list comes back the way the admin left it.
 *
 * Two things, both in sessionStorage (per tab, survives a refresh, gone when
 * the tab closes):
 *
 *   • list state  — per list page: segment tab, search text, sort column and
 *     direction. `loadListState('people', defaults)` on mount,
 *     `saveListState('people', patch)` on every change.
 *   • list context — ONE entry: the ordered ids of the rows the most recently
 *     rendered list showed, plus where that list lives. A detail page asks
 *     `listNeighbours(id)` and gets its position and neighbours, or null when
 *     it wasn't opened from a list (deep link, search).
 *
 * Rendering any list overwrites the context, so Prev / Next always follow
 * the list the admin actually walked in from.
 */

const STATE_PREFIX = 'gear.listState.';
const CONTEXT_KEY  = 'gear.listContext';

function read(key, fallback) {
  try { return JSON.parse(sessionStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
function write(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode / quota — the page still works, it just forgets */ }
}

export function loadListState(key, defaults = {}) {
  const stored = read(STATE_PREFIX + key, {});
  return { ...defaults, ...(stored && typeof stored === 'object' ? stored : {}) };
}

export function saveListState(key, patch) {
  write(STATE_PREFIX + key, { ...loadListState(key), ...patch });
}

/** @param {{ source: string, ids: string[], href: string, label: string }} ctx */
export function setListContext({ source, ids, href, label }) {
  write(CONTEXT_KEY, { source, ids: [...new Set(ids)], href, label });
}

/**
 * Position of `id` in the remembered list.
 * @returns {{ index: number, total: number, prevId: string|null, nextId: string|null, href: string, label: string, source: string } | null}
 */
export function listNeighbours(id) {
  const ctx = read(CONTEXT_KEY, null);
  if (!ctx || !Array.isArray(ctx.ids) || !ctx.ids.length) return null;
  const index = ctx.ids.indexOf(id);
  if (index < 0) return null;
  return {
    index,
    total:  ctx.ids.length,
    prevId: ctx.ids[index - 1] || null,
    nextId: ctx.ids[index + 1] || null,
    href:   ctx.href,
    label:  ctx.label,
    source: ctx.source,
  };
}
